import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material';
import { BiSolidRightArrowAlt } from 'react-icons/bi';
import RoleChip from './RoleChip';
import FeatureTreeChecklist from '@components/FeatureTreeChecklist';
import type { RooftopChangeItem } from '../utils/rooftopChangeResolver';

interface RooftopChangeModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (selectedFeatureIds?: Set<number>) => void;
  changes: RooftopChangeItem[];
  rooftopName?: string;
  userName?: string;
  action: 'add' | 'remove';
  loading?: boolean;
  /** Tiers of the target user (for feature filtering) */
  targetUserTiers?: Set<string>;
}

const RooftopChangeModal: React.FC<RooftopChangeModalProps> = ({
  open,
  onClose,
  onConfirm,
  changes,
  rooftopName,
  userName,
  action,
  loading = false,
  targetUserTiers,
}) => {
  const [featureIds, setFeatureIds] = useState<Set<number>>(new Set());

  const hasFeatureGap = changes.some(c => c.requiresFeatureSelection);
  const hasDriverRevocation = changes.some(c => c.revokesDriverAccess);
  const hasWarning = changes.some(c => c.type === 'warning');

  const handleConfirm = () => {
    onConfirm(hasFeatureGap ? featureIds : undefined);
  };

  const actionLabel = action === 'add' ? 'Grant Access' : 'Remove Access';
  const title = action === 'add'
    ? `Grant ${rooftopName || 'rooftop'} access`
    : `Remove ${rooftopName || 'rooftop'} access`;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {action === 'add' ? 'Adding' : 'Removing'} rooftop access for <strong>{userName || 'this user'}</strong> will
          trigger the following changes:
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {/* Role changes */}
          {changes.filter(c => c.type === 'role-change').map((change, i) => (
            <Box
              key={`role-${i}`}
              sx={{
                p: 1.5,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: '10px',
                backgroundColor: 'action.hover',
              }}
            >
              <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                {change.title}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {change.description}
              </Typography>
              {change.fromRole && change.toRole && (
                <Box display="flex" alignItems="center" gap={1}>
                  <RoleChip role={change.fromRole} />
                  <BiSolidRightArrowAlt size={14} color="#9ca3af" />
                  <RoleChip role={change.toRole} />
                </Box>
              )}
            </Box>
          ))}

          {/* Feature selection */}
          {hasFeatureGap && (
            <Box
              sx={{
                p: 1.5,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: '10px',
                backgroundColor: 'action.hover',
              }}
            >
              <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                Assign Features
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Select which portal features this user should have access to.
              </Typography>
              <Divider sx={{ mb: 1.5 }} />
              <FeatureTreeChecklist
                selectedFeatureIds={featureIds}
                onChange={setFeatureIds}
                variant="switch"
                targetUserTiers={targetUserTiers}
              />
            </Box>
          )}

          {/* Driver access revocation */}
          {changes.filter(c => c.revokesDriverAccess).map((change, i) => (
            <Alert key={`driver-${i}`} severity="warning">
              <Typography variant="body2" fontWeight={600}>{change.title}</Typography>
              <Typography variant="body2">{change.description}</Typography>
            </Alert>
          ))}

          {/* General warnings */}
          {changes.filter(c => c.type === 'warning').map((change, i) => (
            <Alert key={`warning-${i}`} severity="error">
              <Typography variant="body2" fontWeight={600}>{change.title}</Typography>
              <Typography variant="body2">{change.description}</Typography>
            </Alert>
          ))}

          {/* Driver eligibility info */}
          {changes.filter(c => c.type === 'driver-access' && !c.revokesDriverAccess).map((change, i) => (
            <Alert key={`info-${i}`} severity="info">
              <Typography variant="body2" fontWeight={600}>{change.title}</Typography>
              <Typography variant="body2">{change.description}</Typography>
            </Alert>
          ))}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button variant="outlined" color="secondary" onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color={hasDriverRevocation || hasWarning ? 'warning' : 'primary'}
          onClick={handleConfirm}
          disabled={loading}
        >
          {loading ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
          {loading ? 'Applying...' : actionLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RooftopChangeModal;
